import React, { ChangeEvent } from 'react';
import { Col, Row } from 'react-bootstrap';
import { InputSelect, InputPercent } from '~/components/NovosInputs';
import { useClassificacaoIbsCbs } from '../../hooks/useClassificacaoIbsCbs';
import Separator from '~/components/Separator';
import { CustomButtonNovo } from '~/components/Buttons/CustomButtonNovo';
import { CgAdd } from 'react-icons/cg';
import { FaEraser } from 'react-icons/fa';
import { TableIBS } from './IBSTable';
import { selectProps } from '../../types';
import { insertOrUpdate } from '~/utils/masterDetail';
import { nanoid } from 'nanoid';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { formatCurrencyAsText, transformAsCurrency } from '~/utils/functions';
import { ufDefault } from '../../defaultData';

const MySwal = withReactContent(Swal);
export const FormIBS: React.FC = () => {
  const {
    formUF: {
      register,
      control,
      errors,
      setValue,
      handleSubmit,
      clearErrors,
      reset,
    },
    ufOptions,
    setRowsUf,
    masterDetail,
    setMasterDetail,
    setUpdateItem,
    rowsUf,
  } = useClassificacaoIbsCbs();

  const handleConfirmationAllUf = async (): Promise<boolean> => {
    const { isConfirmed } = await MySwal.fire({
      title: 'Deseja replicar valores para todas as UF´s?',
      showCancelButton: true,
      showCloseButton: false,
      showConfirmButton: true,
      confirmButtonColor: '#0065FF',
      cancelButtonColor: '#DE350B',
      showDenyButton: false,
      confirmButtonText: 'SIM',
      cancelButtonText: 'NÃO',
      customClass: { popup: 'swal-wide' },
    });
    return isConfirmed;
  };

  const handleInsert = handleSubmit(async (data) => {
    if (data.des_sigla.value !== 0) {
      const findUF = rowsUf.find(
        (item) => Number(item.des_sigla.value) === Number(data.des_sigla.value),
      );

      const uuid = findUF ? findUF.uuid : nanoid();
      const itens = {
        uuid,
        des_sigla: data.des_sigla,
        per_aliq_ibs_est: transformAsCurrency(data.per_aliq_ibs_est),
        per_aliq_ibs_mun: transformAsCurrency(data.per_aliq_ibs_mun),
        per_red_ibs: transformAsCurrency(data.per_red_ibs),
      };

      const rowsDetail: any = await insertOrUpdate(
        'ufs',
        itens,
        masterDetail,
        setMasterDetail,
      );

      setRowsUf([...rowsDetail]);
      handleClear();
      return setUpdateItem(false);
    }
    // todas ufs
    if (rowsUf.length > 0) {
      const confirm = await handleConfirmationAllUf();
      if (!confirm) return;
    }

    ufOptions
      .filter((item) => Number(item.value) !== 0)
      .map(async (i) => {
        const findUF = rowsUf.find(
          (itemUF) => Number(itemUF.des_sigla.value) === Number(i.value),
        );
        const uuid = findUF ? findUF.uuid : nanoid();
        const ufs = {
          uuid,
          des_sigla: i,
          per_aliq_ibs_est: transformAsCurrency(data.per_aliq_ibs_est),
          per_aliq_ibs_mun: transformAsCurrency(data.per_aliq_ibs_mun),
          per_red_ibs: transformAsCurrency(data.per_red_ibs),
        };

        const rowsDetail: any = await insertOrUpdate(
          'ufs',
          ufs,
          masterDetail,
          setMasterDetail,
        );
        setRowsUf(rowsDetail);
      });
    handleClear();
    setUpdateItem(false);
  });

  const handleChangeDesSigla = (selected: selectProps) => {
    const findUF = rowsUf.find(
      (itemUF) => Number(itemUF.des_sigla.value) === Number(selected.value),
    );
    if (!findUF) reset(ufDefault);
    setValue(
      'per_aliq_ibs_est',
      formatCurrencyAsText(findUF?.per_aliq_ibs_est),
    );
    setValue(
      'per_aliq_ibs_mun',
      formatCurrencyAsText(findUF?.per_aliq_ibs_mun),
    );
    setValue('per_red_ibs', formatCurrencyAsText(findUF?.per_red_ibs));
    setValue('des_sigla', selected);
    clearErrors('des_sigla');
  };

  const handleClear = () => {
    reset(ufDefault);
  };

  return (
    <>
      <Row>
        <Col md={12}>
          <Separator labelText="IBS" />
        </Col>
      </Row>
      <Row className="gx-4 align-items-end">
        <Col md={3} sm={6} xs={12}>
          <InputSelect
            name="des_sigla"
            label="UF *"
            placeholder="Selecione"
            options={ufOptions}
            maxLength={15}
            register={register}
            control={control}
            isError={!!errors.des_sigla}
            changeSelected={(selected: selectProps) => {
              handleChangeDesSigla(selected);
            }}
          />
        </Col>
        <Col md={2} sm={6} xs={12}>
          <InputPercent
            name="per_aliq_ibs_est"
            label="IBS Estadual *"
            placeholder="0,00"
            maxLength={6}
            max={100}
            min={0}
            register={register}
            control={control}
            isError={!!errors.per_aliq_ibs_est}
            onInput={(e: ChangeEvent<HTMLInputElement>) => {
              const { value } = e.target;
              setValue('per_aliq_ibs_est', value);
            }}
          />
        </Col>
        <Col md={2} sm={6} xs={12}>
          <InputPercent
            name="per_aliq_ibs_mun"
            label="IBS Municipal *"
            placeholder="0,00"
            maxLength={6}
            max={100}
            min={0}
            register={register}
            control={control}
            isError={!!errors.per_aliq_ibs_mun}
            onInput={(e: ChangeEvent<HTMLInputElement>) => {
              const { value } = e.target;
              setValue('per_aliq_ibs_mun', value);
            }}
          />
        </Col>
        <Col md={2} sm={6} xs={12}>
          <InputPercent
            name="per_red_ibs"
            label="Redução *"
            placeholder="0,00"
            maxLength={6}
            max={100}
            min={0}
            register={register}
            control={control}
            isError={!!errors.per_red_ibs}
            onInput={(e: ChangeEvent<HTMLInputElement>) => {
              const { value } = e.target;
              setValue('per_red_ibs', value);
            }}
          />
        </Col>
        <Col
          md={3}
          sm={12}
          xs={12}
          className="d-flex justify-content-end gap-2"
        >
          <CustomButtonNovo
            disabled={false}
            onClick={handleInsert}
            label="Inserir"
            icon={CgAdd}
          />
          <CustomButtonNovo
            variant="clear"
            disabled={false}
            onClick={handleClear}
            label="Limpar"
            icon={FaEraser}
          />
        </Col>
      </Row>
      <Row className="mt-4">
        <TableIBS />
      </Row>
    </>
  );
};
